import React, { useState, useEffect } from "react";
import { newsService } from "../services/newsService";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://newgardenbackend.amcfest.in/api";

// Helper to build full image URL and fix old localhost URLs
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  const baseUrl = API_BASE_URL.replace("/api", "");

  // If it's already a full URL, just return (with minimal fixes)
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // Relative path starting with /
  if (imagePath.startsWith("/")) {
    return `${baseUrl}${imagePath}`;
  }

  // Any other relative path
  return `${baseUrl}/${imagePath}`;
};

// File type helpers
const isImageFile = (pathOrName) => {
  if (!pathOrName) return false;
  const lower = pathOrName.toLowerCase();
  return (
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".png") ||
    lower.endsWith(".webp") ||
    lower.endsWith(".gif")
  );
};

const isPdfFile = (pathOrName) => {
  if (!pathOrName) return false;
  return pathOrName.toLowerCase().endsWith(".pdf");
};

const NewsManagement = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    descr: "",
    image: null, // Image file only
    pdf: null,   // PDF file only
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [pdfFileName, setPdfFileName] = useState(null);
  const [viewImageModal, setViewImageModal] = useState({ show: false, src: "", title: "" });

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      setLoading(true);
      const data = await newsService.getAllNews();
      let list = Array.isArray(data) ? data : data?.data || [];

      // Normalize image and PDF URLs in the response (fix localhost → production)
      list = list.map((item) => {
        let image = item.image;
        let pdf = item.pdf;
        if (typeof image === "string" && image) {
          image = image.replace("http://localhost:4001", "https://newgardenbackend.amcfest.in");
          image = image.replace("http://localhost", "https://newgardenbackend.amcfest.in");
        }
        if (typeof pdf === "string" && pdf) {
          pdf = pdf.replace("http://localhost:4001", "https://newgardenbackend.amcfest.in");
          pdf = pdf.replace("http://localhost", "https://newgardenbackend.amcfest.in");
        }
        return { ...item, image, pdf };
      });

      setNewsList(list);
    } catch (error) {
      console.error("Error loading news:", error);
      alert("Failed to load news");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      // Generate preview for image files
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({ ...formData, image: null });
      setImagePreview(null);
    }
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, pdf: file });
      setPdfFileName(file.name);
    } else {
      setFormData({ ...formData, pdf: null });
      setPdfFileName(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title || "");
      formDataToSend.append("descr", formData.descr || "");
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }
      if (formData.pdf) {
        formDataToSend.append("pdf", formData.pdf);
      }
      
      // At least one file should be provided
      if (!formData.image && !formData.pdf && !editingNews) {
        alert("Please select at least one file (Image or PDF)");
        setLoading(false);
        return;
      }

      if (editingNews) {
        await newsService.updateNews(editingNews.id, formDataToSend);
        alert("News updated successfully");
      } else {
        await newsService.addNews(formDataToSend);
        alert("News added successfully");
      }

      resetForm();
      loadNews();
    } catch (error) {
      console.error("Error saving news:", error);
      alert(error.response?.data?.error || error.response?.data?.message || "Failed to save news");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (news) => {
    setEditingNews(news);
    setFormData({
      title: news.title || "",
      descr: news.descr || "",
      image: null,
      pdf: null,
    });
    // Show preview if existing file is an image
    if (news.image && isImageFile(news.image)) {
      setImagePreview(getImageUrl(news.image));
    } else {
      setImagePreview(null);
    }
    // Show PDF filename if exists
    if (news.pdf) {
      const pdfUrl = getImageUrl(news.pdf);
      const fileName = pdfUrl ? pdfUrl.split('/').pop() : null;
      setPdfFileName(fileName);
    } else {
      setPdfFileName(null);
    }
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this news item?")) return;
    try {
      setLoading(true);
      await newsService.deleteNews(id);
      alert("News deleted successfully");
      loadNews();
    } catch (error) {
      console.error("Error deleting news:", error);
      alert("Failed to delete news");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", descr: "", image: null, pdf: null });
    setImagePreview(null);
    setPdfFileName(null);
    setEditingNews(null);
    setShowForm(false);
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">News Management</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          {showForm ? "Cancel" : "Add New News"}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-6 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">
            {editingNews ? "Edit News" : "Add New News"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter news title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="descr"
                value={formData.descr}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter news description (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {imagePreview && (
                <div className="mt-4">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-w-xs h-48 object-cover rounded-lg border border-gray-300 shadow-md"
                    onError={(e) => {
                      console.error("News preview image load error:", e.target.src);
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
              {editingNews && editingNews.image && !imagePreview && (
                <div className="mt-2 text-sm text-gray-600">
                  Current image: <a href={getImageUrl(editingNews.image)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{getImageUrl(editingNews.image)?.split('/').pop()}</a>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PDF
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePdfChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {pdfFileName && (
                <div className="mt-3 inline-flex items-center px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
                  <span className="font-semibold mr-2">PDF selected:</span>
                  <span>{pdfFileName}</span>
                </div>
              )}
              {editingNews && editingNews.pdf && !pdfFileName && (
                <div className="mt-2 text-sm text-gray-600">
                  Current PDF: <a href={getImageUrl(editingNews.pdf)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{getImageUrl(editingNews.pdf)?.split('/').pop()}</a>
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? "Saving..." : editingNews ? "Update" : "Add"} News
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* News List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading && !showForm ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading news...</p>
          </div>
        ) : newsList.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No news found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Image
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PDF
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {newsList.map((news) => (
                  <tr key={news.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {news.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {news.image ? (
                        <img
                          src={getImageUrl(news.image)}
                          alt={news.title || "News"}
                          className="h-20 w-20 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity shadow-md"
                          onClick={() =>
                            setViewImageModal({
                              show: true,
                              src: getImageUrl(news.image),
                              title: news.title || "News Image",
                            })
                          }
                          onError={(e) => {
                            console.error("News image load error:", e.target.src);
                            e.target.style.display = "none";
                            e.target.onclick = null;
                          }}
                        />
                      ) : (
                        <div className="h-20 w-20 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500 border border-gray-300">
                          No Image
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {news.pdf ? (
                        <div className="h-20 w-28 bg-red-50 rounded-lg flex flex-col items-center justify-center text-xs border border-red-300 text-red-700">
                          <div className="font-bold text-sm mb-1">PDF</div>
                          <button
                            type="button"
                            onClick={() => window.open(getImageUrl(news.pdf), "_blank")}
                            className="px-2 py-1 bg-red-600 text-white rounded text-[11px] hover:bg-red-700"
                          >
                            View / Download
                          </button>
                        </div>
                      ) : (
                        <div className="h-20 w-28 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500 border border-gray-300">
                          No PDF
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {news.title || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                      <div className="truncate" title={news.descr || "N/A"}>
                        {news.descr || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {news.created_at
                        ? new Date(news.created_at).toLocaleString()
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(news)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(news.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Image View Modal */}
      {viewImageModal.show && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setViewImageModal({ show: false, src: "", title: "" })}
        >
          <div
            className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">{viewImageModal.title}</h3>
              <button
                onClick={() => setViewImageModal({ show: false, src: "", title: "" })}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <img
                src={getImageUrl(viewImageModal.src)}
                alt={viewImageModal.title}
                className="max-w-full h-auto rounded-lg shadow-lg"
                onError={(e) => {
                  console.error("News modal image load error:", e.target.src);
                  e.target.style.display = "none";
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;



